const SITE = "https://manasneuropsychiatryhospital.in";

const clinicNode = {
  "@type": ["MedicalOrganization", "Hospital"],
  "@id": `${SITE}/#organization`,
  name: "Manas Neuropsychiatry Hospital",
  url: SITE,
  telephone: "+91-94275-60101",
  medicalSpecialty: ["Psychiatry", "Neuropsychiatry", "Addiction Medicine", "Sexology"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
    addressLocality: "Bhavnagar",
    postalCode: "364001",
    addressRegion: "Gujarat",
    addressCountry: "IN",
  },
};

const faqNode = (faqs) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const conditions = {
  "epilepsy-depression": {
    slug: "epilepsy-depression",
    eyebrow: "Epilepsy with Depression · Neuropsychiatry",
    h1a: "Fits are not the whole illness.",
    h1b: "The mood is half of it.",
    seoTitle: "Epilepsy with Depression Treatment in Bhavnagar — Neuropsychiatrist",
    seoDescription:
      "Depression and anxiety in epilepsy, treated in Bhavnagar at Manas Neuropsychiatry Hospital (MD Neuropsychiatry, since 1989). Which antidepressants are safe with anti-seizure drugs, mood side-effects of levetiracetam, post-ictal depression, and functional seizures. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/ffa7acc90dd9c074664d3995b23ebfbaa8926f64ba7202645235c4e888a49c09.jpeg",
    imageAlt: "A young man with epilepsy sitting quietly at home beside his medicine strip in Gujarat",
    caption: "Roughly one in three people with epilepsy also has depression — and it is treated far less often than the seizures.",
    intro:
      "Depression is the commonest psychiatric condition accompanying epilepsy, affecting around a third of patients, and it damages quality of life even more than the seizure frequency does. Yet it is routinely left untreated — either because the family believes low mood is natural in epilepsy, or because someone has warned that psychiatric medicines cause fits. Both beliefs are wrong, and both are expensive. The right antidepressant, at the right dose, alongside the right anti-seizure drug, is entirely safe.",
    symptomGroups: [
      {
        label: "Mood & thought",
        items: [
          "Low mood, hopelessness and loss of interest between seizures",
          "Irritability and short temper that the family blames on the illness",
          "Guilt and shame about the diagnosis, hiding it from relatives and employers",
          "Fear of the next fit, avoiding travel, cooking, swimming or working alone",
          "Suicidal thoughts — the risk in epilepsy is several times the general population",
        ],
      },
      {
        label: "Around the seizure itself",
        items: [
          "Low mood or tearfulness for hours to days after a seizure (post-ictal depression)",
          "Anxiety, dread or strange fear in the minutes before a fit (pre-ictal)",
          "Confusion, aggression or wandering immediately after a seizure",
          "Psychosis appearing days after a cluster of seizures",
          "Memory and concentration difficulty, worse with poorly controlled epilepsy",
        ],
      },
      {
        label: "Medication-related",
        items: [
          "Irritability, aggression and depression on levetiracetam",
          "Slowing, weight change and low mood on topiramate, phenobarbitone or valproate",
          "Mood swings after rapid withdrawal of an anti-seizure drug",
          "Fatigue and sleepiness mistaken for depression",
        ],
      },
    ],
    causes: [
      "Shared brain biology — temporal and frontal lobe circuits regulate both seizures and mood",
      "Poor seizure control, frequent seizures and injuries from falls",
      "Side-effects of specific anti-seizure medicines, especially levetiracetam, topiramate and phenobarbitone",
      "Stigma: refused marriage proposals, job loss, being told to hide the diagnosis",
      "Restrictions on driving, work and independence, and financial strain",
      "Sleep deprivation and alcohol, which worsen both mood and seizure threshold",
      "Thyroid disease, vitamin D and B12 deficiency, common in long-term anti-seizure drug use",
    ],
    treatments: [
      {
        title: "Review the epilepsy first",
        desc: "Seizure type, frequency, triggers, drug levels and adherence are reviewed, along with the EEG and imaging. Better seizure control by itself lifts mood in a significant number of patients.",
      },
      {
        title: "Check the medicine before adding another",
        desc: "If depression or irritability began after levetiracetam or topiramate was started, the answer is often a switch rather than an antidepressant. This single step resolves a large share of the cases we see.",
      },
      {
        title: "Antidepressants chosen for a brain with epilepsy",
        desc: "SSRIs such as sertraline and escitalopram are the preferred choices and do not meaningfully increase seizure risk at therapeutic doses. Bupropion and high-dose tricyclics are avoided. Interactions with enzyme-inducing drugs like carbamazepine and phenytoin are accounted for in dosing.",
      },
      {
        title: "Psychological therapy and seizure-anxiety work",
        desc: "CBT for the fear of the next seizure, graded return to the activities that have been abandoned, and work on shame and disclosure within the family and the workplace.",
      },
      {
        title: "Distinguishing functional seizures from epileptic ones",
        desc: "Some patients have both. Where events are dissociative rather than epileptic, piling on more anti-seizure drugs harms; explanation and psychological therapy help. Careful history, event description and EEG settle this.",
      },
      {
        title: "Family education and rights",
        desc: "Relatives are taught first-aid for a seizure, what epilepsy does and does not prevent, and that epilepsy has not been a legal bar to marriage in India since 1999. Most families have never been told any of this.",
        link: { to: "/neuropsychiatry", label: "Our neuropsychiatry service" },
      },
    ],
    urgent:
      "A seizure lasting more than five minutes, or repeated seizures without regaining consciousness in between, is status epilepticus — go to the nearest emergency room immediately. Also treat any talk of suicide as an emergency and call +91 94275 60101 the same day.",
    faqs: [
      { q: "Will antidepressants cause more fits?", a: "At therapeutic doses, modern SSRIs such as sertraline and escitalopram do not meaningfully increase seizure risk — and some evidence suggests treating the depression improves seizure control, partly through better sleep and better adherence. The medicines genuinely best avoided are bupropion and high doses of older tricyclics. Refusing all psychiatric treatment 'because of fits' leaves a treatable illness untreated." },
      { q: "Is epilepsy a mental illness?", a: "No. Epilepsy is a neurological condition caused by abnormal electrical activity in the brain, and most people with epilepsy have entirely normal intelligence. It is also not caused by possession, black magic or a curse. What is true is that depression and anxiety occur alongside epilepsy far more often than in the general population — and those are treatable too." },
      { q: "My son became short-tempered and aggressive after a new epilepsy medicine was started. Is that a coincidence?", a: "Very unlikely. Levetiracetam in particular is well known for irritability, aggression and low mood, and topiramate and phenobarbitone have their own behavioural effects. Do not stop the drug on your own — bring the prescription and we will review whether a switch or a dose change is the right answer." },
      { q: "He is depressed only for a day or two after each fit. Is that still depression?", a: "That is post-ictal depression, and it is a recognised part of the illness rather than a separate weakness. If it is frequent or severe, improving seizure control and treating the mood symptoms both help." },
      { q: "The EEG and MRI are normal but the fits continue. What does that mean?", a: "It may mean the epilepsy is simply not captured on a routine EEG — or it may mean the events are dissociative (functional) seizures, which are genuine and involuntary but not electrical. The distinction matters enormously, because the second kind does not respond to anti-seizure drugs and does respond to explanation and psychological therapy." },
      { q: "Can a person with epilepsy marry and have children?", a: "Yes. Epilepsy was removed as a ground for annulment from Indian marriage law in 1999. Pregnancy needs planning — folic acid before conception and avoiding valproate where possible — and that planning is exactly what a consultation is for." },
      { q: "Do we need to see a neurologist as well?", a: "Often yes, and we work with them. Our role is the overlap: the mood, behaviour, memory and medication side-effects alongside the seizures, so you are not sent back and forth between two clinics with each side treating only half the problem." },
    ],
    related: [
      { to: "/neuropsychiatry", label: "Neuropsychiatry service" },
      { to: "/conditions/depression", label: "Depression treatment" },
      { to: "/conditions/post-stroke-depression", label: "Post-stroke depression" },
    ],
    condName: "Depression in Epilepsy",
    procedures: ["Epilepsy psychiatric care", "Antidepressant management in epilepsy", "Cognitive behavioural therapy", "Anti-seizure medication review", "Family psychoeducation"],
  },

  "post-stroke-depression": {
    slug: "post-stroke-depression",
    eyebrow: "Post-Stroke Depression · Neuropsychiatry",
    h1a: "He is not being stubborn.",
    h1b: "The stroke took his mood too.",
    seoTitle: "Post-Stroke Depression Treatment in Bhavnagar — Neuropsychiatrist",
    seoDescription:
      "Post-stroke depression and apathy treated in Bhavnagar at Manas Neuropsychiatry Hospital (MD Neuropsychiatry, since 1989). Low mood, no interest in physiotherapy, uncontrollable crying, vascular cognitive impairment — assessed and treated so rehabilitation finally works. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/8d3a2b312ca2736e0791a59db17f7c24a81e93e3030ef7f724e6a79e9b3880b5.jpeg",
    imageAlt: "An elderly stroke survivor sitting listlessly at home while his daughter looks on with concern",
    caption: "About one in three stroke survivors develops depression — and treating it measurably improves physical recovery.",
    intro:
      "Depression after a stroke is not simply sadness about what has happened; it is a direct consequence of injury to the brain's mood circuits, and it affects roughly a third of survivors. Left untreated it slows physiotherapy, deepens disability and even raises mortality. Treated, patients engage with rehabilitation again — which is why this is one of the most worthwhile and most neglected treatments in stroke care.",
    symptomGroups: [
      {
        label: "Mood & motivation",
        items: [
          "Low mood, tearfulness and hopelessness since the stroke",
          "Apathy — no interest, no initiative, sitting all day, which families read as laziness",
          "Refusing or skipping physiotherapy and speech therapy",
          "Feeling a burden on the family, talk of being better off dead",
          "Irritability and anger outbursts that are new to the person",
        ],
      },
      {
        label: "Distinctive after-stroke features",
        items: [
          "Uncontrollable crying or laughing with little feeling behind it (emotional incontinence)",
          "Catastrophic reaction — sudden distress when a task cannot be done",
          "Anxiety and constant fear of a second stroke",
          "Sleep reversal, waking at night and dozing all day",
          "Memory, attention and planning difficulty — vascular cognitive impairment",
        ],
      },
      {
        label: "Physical overlap",
        items: [
          "Fatigue out of proportion to the physical weakness",
          "Loss of appetite and weight",
          "Increased pain complaints in the affected limb",
          "Poor compliance with blood-pressure, diabetes and anticoagulant medication",
        ],
      },
    ],
    causes: [
      "Direct injury to frontal and basal ganglia mood circuits — biology, not attitude",
      "Inflammation and neurotransmitter changes following the infarct or bleed",
      "Loss of function, independence, driving, work and role within the family",
      "Communication loss in aphasia, which isolates the patient inside the house",
      "Social isolation after the early flood of visitors stops",
      "Carer exhaustion and conflict within the household",
      "Untreated pain, poor sleep, and vitamin B12 or thyroid abnormalities",
    ],
    treatments: [
      {
        title: "Assessment that separates apathy from depression",
        desc: "The two look identical to a family but need different handling, and apathy can occur with a perfectly normal mood. Aphasia-friendly assessment is used where speech is affected, together with the family's account of the change.",
      },
      {
        title: "Review the medical picture",
        desc: "Blood pressure, sugar, thyroid, B12, sleep apnoea, pain and a check on every current medicine — some blood-pressure and pain drugs worsen low mood, and untreated pain alone can masquerade as depression.",
      },
      {
        title: "Antidepressants chosen for the stroke patient",
        desc: "SSRIs such as sertraline and escitalopram are preferred, started low and increased slowly, with interactions with anticoagulants and antiplatelets taken into account. Emotional incontinence in particular responds quickly and often dramatically to a low dose.",
      },
      {
        title: "Therapy and activity rebuilding",
        desc: "Small graded goals tied to rehabilitation targets, behavioural activation, and problem-solving therapy adapted for communication difficulty — delivered alongside physiotherapy rather than instead of it.",
      },
      {
        title: "rTMS for resistant cases and rehabilitation support",
        desc: "Where medication is limited by side-effects or several other illnesses, rTMS is available in our own suite in Bhavnagar, and is also used to support motor and cognitive recovery after stroke.",
        link: { to: "/therapies/rtms", label: "rTMS treatment guide" },
      },
      {
        title: "Carer support and second-stroke prevention",
        desc: "The family is taught the difference between 'cannot' and 'will not', how to set realistic daily targets, and how to protect their own health. Strict control of blood pressure, sugar and medication adherence is part of the mental health plan, not separate from it.",
      },
    ],
    urgent:
      "Sudden new weakness, facial droop, slurred speech or confusion means another stroke — go to the nearest hospital immediately. Any talk of suicide in a stroke survivor is an emergency: call +91 94275 60101 the same day.",
    faqs: [
      { q: "Is low mood not natural after a stroke? Why treat it?", a: "Some sadness is expected; persistent depression is not, and it is a direct consequence of the brain injury as much as of the circumstances. It affects roughly a third of survivors, slows physical recovery, worsens long-term disability and increases mortality. Treating it improves rehabilitation outcomes — 'of course he is sad' is the sentence that keeps patients untreated for years." },
      { q: "My father sits all day and refuses physiotherapy. Is he depressed or just stubborn?", a: "Most likely neither — this is usually apathy, a distinct consequence of injury to specific brain circuits that can occur with or without depression. He genuinely cannot summon the initiative, however much he is scolded, and scolding deepens the problem. It is assessable and treatable, and treating it is often what makes physiotherapy finally work." },
      { q: "He cries suddenly for no reason and then stops. Is he becoming mentally unstable?", a: "That is emotional incontinence (pseudobulbar affect) — a loss of control over the expression of emotion rather than overwhelming sadness, caused by the stroke itself. It is one of the most satisfying symptoms to treat: a low dose of an SSRI often reduces it substantially within one to two weeks." },
      { q: "Is it safe to add an antidepressant to blood thinners and blood-pressure medicines?", a: "Yes, with the right choice and monitoring. SSRIs can slightly increase bleeding risk alongside anticoagulants and antiplatelets, so the drug and dose are selected with that in mind and reviewed. The risk of leaving depression untreated after a stroke is considerably greater." },
      { q: "He cannot speak properly after the stroke. Can you still assess his mood?", a: "Yes. Aphasia-adapted assessment uses the family's observations, behaviour, sleep, appetite, facial expression and simple yes/no and visual scales. Patients with aphasia are at especially high risk precisely because they cannot tell anyone how they feel." },
      { q: "When should treatment start?", a: "As soon as the depression is identified — commonly within the first three to six months, when the risk peaks, although it can appear at any time in the first two years. Earlier treatment means more of the rehabilitation window is actually used." },
      { q: "Is memory loss after a stroke the same as dementia?", a: "Not necessarily. Vascular cognitive impairment after a stroke can remain stable or even improve, particularly once blood pressure, sugar and mood are controlled. Whether it is progressing towards vascular dementia is a question for formal assessment rather than assumption." },
      { q: "Do you come home, or must the patient be brought in?", a: "Bring them to the OPD where at all possible — examination and cognitive assessment need the clinic. Where mobility is a real barrier, call +91 94275 60101 and we will advise on the practicalities, including which family member should accompany and which reports to carry." },
    ],
    related: [
      { to: "/neuropsychiatry", label: "Neuropsychiatry service" },
      { to: "/conditions/dementia", label: "Dementia & memory loss" },
      { to: "/therapies/rtms", label: "rTMS treatment" },
    ],
    condName: "Post-Stroke Depression",
    procedures: ["Post-stroke depression treatment", "Apathy assessment", "Antidepressant management after stroke", "Cognitive assessment", "Repetitive transcranial magnetic stimulation (rTMS)"],
  },

  depression: {
    slug: "depression",
    eyebrow: "Depression Treatment · OPD",
    h1a: "Depression is not weakness.",
    h1b: "It is treatable.",
    seoTitle: "Depression Treatment in Bhavnagar — Psychiatrist for Depression",
    seoDescription:
      "Depression treatment in Bhavnagar at Manas Neuropsychiatry Hospital (since 1989). Symptoms, causes, medication, CBT and rTMS for treatment-resistant depression. OPD Mon–Sat 9 AM–8 PM, no referral needed. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/67132e619a5f3466944f7e8f3d5ae26bf0645cc6ae3929c8e642f1291d67e367.jpeg",
    imageAlt: "A young woman sitting quietly by a window in a home in Gujarat",
    caption: "Around one in twenty adults has depression in any given year. Most recover fully with treatment.",
    intro:
      "Depression is a medical illness of mood, sleep, energy and thought — not a character flaw and not something a person can simply decide to stop. It responds well to treatment: the majority of patients we see in OPD are substantially better within six to eight weeks, and most eventually stop medication altogether under supervision.",
    symptomGroups: [
      {
        label: "Mood & thought",
        items: [
          "Low mood most of the day, most days",
          "Loss of interest in work, family and things once enjoyed",
          "Persistent guilt, self-blame or feeling a burden",
          "Hopelessness about the future",
          "Thoughts of death, self-harm or suicide",
        ],
      },
      {
        label: "Body & behaviour",
        items: [
          "Waking at 3–4 AM and being unable to sleep again, or sleeping excessively",
          "Loss of appetite and weight, or comfort-eating",
          "Constant fatigue, heaviness in the limbs",
          "Body aches, headache and stomach complaints with normal reports",
          "Slowed speech and movement, or restlessness",
          "Withdrawal from relatives, phone calls and social occasions",
        ],
      },
    ],
    causes: [
      "Family history of depression or bipolar disorder",
      "Thyroid disease, vitamin B12 or D deficiency, anaemia, diabetes and post-viral states",
      "Alcohol and cannabis use — a very common and reversible cause",
      "Chronic stress: financial loss, examination failure, marital conflict, caregiving burden",
      "Bereavement, migration or retirement",
      "Post-partum period, and medications such as steroids or some blood-pressure drugs",
    ],
    treatments: [
      {
        title: "Assessment, and ruling out physical causes",
        desc: "A 45–60 minute consultation with a structured severity assessment, plus blood tests for thyroid, haemoglobin, sugar and B12 where indicated — because treating a thyroid problem as depression helps nobody.",
      },
      {
        title: "Antidepressant medication where indicated",
        desc: "Modern antidepressants (SSRIs/SNRIs) are not sedatives, are not addictive, and are usually taken for 6–12 months and then tapered. Dose, side-effects and duration are explained before you start.",
      },
      {
        title: "Cognitive Behavioural Therapy (CBT)",
        desc: "Structured sessions that change the thought patterns and avoidance keeping the depression in place. For mild depression, therapy alone is often enough.",
      },
      {
        title: "Behavioural activation & sleep correction",
        desc: "Small, specific daily targets and a fixed sleep-wake routine — the least glamorous and most reliably effective part of recovery.",
      },
      {
        title: "Family psychoeducation",
        desc: "Relatives are taught what depression is and what unhelpful advice sounds like. Family support changes outcomes measurably.",
      },
      {
        title: "rTMS or ketamine for treatment-resistant depression",
        desc: "If two adequate medication trials have failed, we offer FDA-approved rTMS and, in severe or suicidal cases, ketamine infusion — in our own neuromodulation suite in Bhavnagar.",
        link: { to: "/neuromodulation", label: "Explore neuromodulation" },
      },
    ],
    urgent:
      "If you or someone you know is talking about suicide, giving away belongings, or has already attempted self-harm, treat it as a medical emergency. Call +91 94275 60101 for same-day assessment.",
    faqs: [
      { q: "Is depression treatable without admission?", a: "Yes. The large majority of depression is treated entirely in OPD — you attend a consultation, start treatment and return for reviews every 2–4 weeks. Admission is reserved for severe depression with suicide risk, refusal to eat, or psychotic symptoms." },
      { q: "Are antidepressants habit-forming?", a: "No. Antidepressants are not addictive and do not cause a 'high'. They are usually continued for 6–12 months after recovery to prevent relapse, and then tapered gradually under supervision." },
      { q: "How long before I start feeling better?", a: "Sleep and appetite often improve in the first 1–2 weeks; mood and interest typically lift between weeks 3 and 6. Full recovery in 6–12 weeks is the usual pattern with regular follow-up." },
      { q: "Can depression be cured with counselling alone?", a: "Mild depression frequently responds to CBT alone. Moderate to severe depression does better with medication plus therapy — we will tell you honestly which category you fall into." },
      { q: "What if medicines have not worked for me before?", a: "That is called treatment-resistant depression and it has specific answers: dose optimisation, augmentation, rTMS, or ketamine infusion. Manas is among the few centres in Saurashtra offering all of these." },
      { q: "Will my employer or family find out?", a: "No. Your records are confidential and are never shared without your written consent." },
      { q: "Do you treat depression in elderly patients and after childbirth?", a: "Yes — late-life depression and post-partum depression are both common and both treated here, with medication choices appropriate to age and breastfeeding." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/anxiety", label: "Anxiety & panic disorder" },
      { to: "/neuromodulation", label: "rTMS for resistant depression" },
    ],
    condName: "Major Depressive Disorder",
    procedures: ["Depression treatment", "Cognitive behavioural therapy", "Antidepressant management", "Repetitive transcranial magnetic stimulation (rTMS)", "Ketamine infusion therapy"],
  },

  anxiety: {
    slug: "anxiety",
    eyebrow: "Anxiety & Panic Disorder · OPD",
    h1a: "Your heart is fine.",
    h1b: "Your anxiety needs treating.",
    seoTitle: "Anxiety & Panic Attack Treatment in Bhavnagar — Psychiatrist",
    seoDescription:
      "Anxiety and panic attack treatment in Bhavnagar at Manas Neuropsychiatry Hospital. Palpitations, breathlessness, fear of dying, health anxiety, social anxiety and phobias — treated with CBT and medication in OPD. No referral needed. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/ff165f1cfb2c8eb1a6bcfa6d1a05baf195c2375d1aa6eb3516f3c6bc1b2e5f12.jpeg",
    imageAlt: "A man sitting calmly at home with his hand on his chest, breathing slowly",
    caption: "Most panic patients reach a cardiologist first. Once the ECG is normal, this is where the answer lies.",
    intro:
      "A panic attack feels exactly like a heart attack — and that is why so many of our patients arrive after a normal ECG, an angiography or three trips to casualty. Anxiety disorders are among the most treatable conditions in all of medicine: with cognitive behavioural therapy, and medication where needed, most people are functioning normally again within eight to twelve weeks.",
    symptomGroups: [
      {
        label: "During a panic attack",
        items: [
          "Sudden palpitations and pounding heart",
          "Breathlessness, or feeling unable to take a full breath",
          "Chest tightness or pain",
          "Trembling, sweating, chills or hot flushes",
          "Dizziness, feeling faint or unreal",
          "Numbness or tingling in the hands and face",
          "An overwhelming fear of dying or losing control",
        ],
      },
      {
        label: "Between attacks",
        items: [
          "Constant worry about work, money, health and family",
          "Restlessness and inability to relax",
          "Avoiding travel, driving, lifts, crowds or being alone",
          "Repeatedly checking the pulse or blood pressure, seeking medical reassurance",
          "Fear of speaking in public or meeting people (social anxiety)",
          "Specific phobias — heights, needles, examinations, animals",
          "Disturbed sleep, irritability, poor concentration, acidity and IBS-like symptoms",
        ],
      },
    ],
    causes: [
      "Genetic sensitivity of the body's alarm system",
      "Thyroid overactivity, anaemia, low vitamin B12 and low blood sugar",
      "Excess caffeine, tobacco, alcohol withdrawal and cannabis",
      "Prolonged stress at work, in examinations or in a marriage",
      "A previous serious illness in self or family — a frequent trigger of health anxiety",
    ],
    treatments: [
      {
        title: "Medical clearance, then a clear explanation",
        desc: "We review your ECG and blood reports, examine you, and then explain in plain language exactly what a panic attack is and why it cannot harm your heart. For many patients this single conversation reduces attack frequency.",
      },
      {
        title: "Cognitive Behavioural Therapy with exposure",
        desc: "The core treatment. You learn to stop avoiding, to re-interpret bodily sensations correctly, and to deliberately face the situations you have been escaping — with a therapist guiding each step.",
      },
      {
        title: "Breathing retraining & relaxation",
        desc: "Slow diaphragmatic breathing and progressive muscular relaxation, practised daily, break the hyperventilation cycle that drives most physical symptoms.",
      },
      {
        title: "Medication where it is genuinely needed",
        desc: "SSRIs are the long-term treatment of choice. Short-acting anti-anxiety tablets are used sparingly and briefly — we actively help patients who have become dependent on them to taper off safely.",
      },
      {
        title: "Lifestyle correction",
        desc: "Caffeine and tobacco reduction, regular exercise, fixed sleep timings and screen discipline. Small, boring, effective.",
      },
      {
        title: "Follow-up until you are functioning, not just calmer",
        desc: "Reviews every 2–4 weeks, with the goal of you travelling, working and living without planning your day around fear.",
      },
    ],
    urgent:
      "Chest pain should always be medically assessed the first time. If you have never had cardiac evaluation, get an ECG first — then come to us with the report.",
    faqs: [
      { q: "Can a panic attack cause a heart attack or death?", a: "No. A panic attack is a surge of adrenaline. It is intensely unpleasant but it does not damage the heart and it is not dangerous. Chest pain should be medically checked once; after that, repeated investigations tend to feed the anxiety." },
      { q: "Is anxiety curable or will I need tablets forever?", a: "Anxiety disorders are highly treatable. Most patients take medication for 9–12 months alongside therapy and then taper off. The CBT skills you learn remain with you permanently." },
      { q: "I have been taking alprazolam for years. Can I stop?", a: "Yes, but never abruptly. We replace it with a safer long-term medication and taper the alprazolam gradually over weeks — a routine part of our OPD practice." },
      { q: "How many therapy sessions will I need?", a: "Typically 8–12 sessions for panic disorder, sometimes fewer. Sessions are usually weekly at first, then spaced out." },
      { q: "Can anxiety cause physical symptoms like acidity and body pain?", a: "Absolutely. Gas, acidity, IBS-like bowel changes, tension headache, neck and shoulder pain, tingling and giddiness are all common physical expressions of anxiety — and they settle as the anxiety is treated." },
      { q: "Do you treat exam stress and social anxiety in students?", a: "Yes, and often briefly. Exam anxiety, stage fear and social anxiety respond very well to a few structured sessions, with or without short-term medication." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/depression", label: "Depression treatment" },
      { to: "/conditions/ocd", label: "OCD treatment" },
    ],
    condName: "Anxiety Disorder and Panic Disorder",
    procedures: ["Panic disorder treatment", "Generalised anxiety disorder treatment", "Cognitive behavioural therapy", "Social anxiety treatment", "Benzodiazepine tapering"],
  },

  ocd: {
    slug: "ocd",
    eyebrow: "OCD Treatment · OPD & rTMS",
    h1a: "It is not cleanliness.",
    h1b: "It is a treatable illness.",
    seoTitle: "OCD Treatment in Bhavnagar — Psychiatrist for Obsessive Compulsive Disorder",
    seoDescription:
      "OCD treatment in Bhavnagar at Manas Neuropsychiatry Hospital. Washing, checking, counting, intrusive thoughts and doubts treated with ERP therapy, high-dose SSRIs and FDA-approved rTMS for resistant OCD. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/4f606c10bfdc3792ed3d5c100e0bad97ca9cb9d988946dbcc893419ef71bf8e8.jpeg",
    imageAlt: "Hands arranging small everyday objects in precise rows on a wooden table",
    caption: "OCD steals hours, not habits. On average, patients wait 7 years before asking for help.",
    intro:
      "Obsessive Compulsive Disorder is not a preference for tidiness. It is a condition in which unwanted thoughts (obsessions) create intense distress, and repeated acts (compulsions) are performed to relieve it — consuming hours of every day. It is one of the conditions where the right treatment makes the most dramatic difference, and where the wrong treatment, or none, causes years of avoidable suffering.",
    symptomGroups: [
      {
        label: "Obsessions — the thoughts",
        items: [
          "Fear of contamination, dirt, germs or illness",
          "Doubt about whether the gas, lock or tap was really turned off",
          "Intrusive blasphemous, violent or sexual thoughts that feel alien and shameful",
          "Need for symmetry, exactness or things to feel 'just right'",
          "Fear of harming someone by accident or negligence",
          "Repeated doubts about religious rituals or purity",
        ],
      },
      {
        label: "Compulsions — the acts",
        items: [
          "Washing hands, bathing or cleaning for long periods",
          "Checking locks, gas, switches or documents repeatedly",
          "Counting, repeating words or actions a fixed number of times",
          "Arranging and re-arranging objects",
          "Seeking reassurance from family again and again",
          "Mental rituals — silently praying or reviewing events to cancel a thought",
          "Avoiding touching, cooking, or leaving the house altogether",
        ],
      },
    ],
    causes: [
      "Genetic and family predisposition — OCD frequently runs in families",
      "Differences in the brain's cortico-striatal circuitry (which is why rTMS helps)",
      "Onset or worsening after stress, childbirth, or a serious illness",
      "Sometimes beginning in childhood or adolescence and mistaken for stubbornness",
    ],
    treatments: [
      {
        title: "Diagnosis and severity scoring",
        desc: "A detailed assessment using standard rating scales, so that improvement can be measured objectively rather than guessed at — and so hoarding, tics or body-image disorders are not missed.",
      },
      {
        title: "Exposure & Response Prevention (ERP)",
        desc: "The single most effective psychological treatment for OCD. You gradually face the trigger and deliberately do not perform the ritual, with the therapist's support, until the anxiety falls on its own.",
      },
      {
        title: "High-dose SSRI medication",
        desc: "OCD needs higher doses and longer trials (10–12 weeks) than depression. Under-dosing and stopping early are the two most common reasons patients believe 'medicines don't work for me'.",
      },
      {
        title: "Augmentation for partial response",
        desc: "Where SSRIs help only partly, low-dose augmenting agents or clomipramine are added — always with a clear plan and review date.",
      },
      {
        title: "rTMS for treatment-resistant OCD",
        desc: "Repetitive transcranial magnetic stimulation is FDA-approved for OCD and available at Manas. It is non-invasive, requires no anaesthesia, and is done as a daily 30-minute OPD sitting.",
        link: { to: "/neuromodulation", label: "See our rTMS suite" },
      },
      {
        title: "Family training",
        desc: "Families are often pulled into the rituals — answering the same question fifty times, or cleaning on the patient's behalf. We teach relatives how to stop accommodating the OCD without becoming harsh.",
      },
    ],
    urgent:
      "Intrusive violent or sexual thoughts are a symptom of OCD, not an intention, and they do not mean you are dangerous. They are among the most treatable and most under-reported forms of OCD.",
    faqs: [
      { q: "Is OCD the same as being a perfectionist or clean person?", a: "No. A clean person chooses to clean. In OCD the act is driven by intense distress, is excessive, cannot be resisted, and consumes hours — damaging work, studies and relationships." },
      { q: "Can OCD be cured?", a: "OCD is very treatable. With adequate-dose medication plus ERP therapy, most patients achieve a 60–80% reduction in symptoms and get their day back. Some need long-term maintenance medication to stay well." },
      { q: "How long does OCD treatment take?", a: "Medication needs 10–12 weeks at an adequate dose before it is judged, and ERP typically runs 12–20 sessions. Improvement is gradual and measurable rather than sudden." },
      { q: "I get horrible thoughts about my own family. Am I going mad?", a: "No. Unwanted intrusive thoughts are a classic OCD symptom, precisely because they are the opposite of what you value. Patients who have these thoughts are not dangerous, and this form of OCD responds well to treatment." },
      { q: "What is rTMS and do I need it for OCD?", a: "rTMS uses focused magnetic pulses to modulate the brain circuit involved in OCD. It is reserved for patients who have not responded adequately to medication and therapy, and it is done as a daily outpatient sitting with no anaesthesia." },
      { q: "My child keeps washing hands and repeating things. Should I wait?", a: "Do not wait. Childhood OCD responds better and faster the earlier it is treated, and early treatment prevents years of academic and social damage." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/anxiety", label: "Anxiety & panic disorder" },
      { to: "/neuromodulation", label: "rTMS for resistant OCD" },
    ],
    condName: "Obsessive Compulsive Disorder",
    procedures: ["OCD treatment", "Exposure and response prevention therapy", "SSRI pharmacotherapy", "Repetitive transcranial magnetic stimulation (rTMS) for OCD"],
  },

  insomnia: {
    slug: "insomnia",
    eyebrow: "Insomnia & Sleep Disorders · OPD",
    h1a: "Sleeping pills are not",
    h1b: "a treatment plan.",
    seoTitle: "Insomnia Treatment in Bhavnagar — Sleep Specialist Psychiatrist",
    seoDescription:
      "Insomnia and sleep disorder treatment in Bhavnagar at Manas Neuropsychiatry Hospital. CBT-I therapy, safe tapering of sleeping pills, screening for sleep apnoea, thyroid disease and depression. OPD Mon–Sat. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/d2546b893ca4b08e43bca036f10cb74c11b83006dbebf5bf7e146c91a3d5580a.jpeg",
    imageAlt: "A man lying awake at night in a home in Gujarat",
    caption: "Chronic insomnia is almost never just about sleep. We treat the cause, then the sleep follows.",
    intro:
      "Most people with long-standing insomnia have been handed a sleeping tablet without anyone asking why they cannot sleep. Insomnia is usually a symptom — of anxiety, depression, thyroid disease, pain, sleep apnoea, alcohol use, shift work, or a sleep routine that has quietly collapsed. Cognitive Behavioural Therapy for Insomnia (CBT-I) outperforms sleeping pills in the long run, and we use it as the backbone of treatment.",
    symptomGroups: [
      {
        label: "At night",
        items: [
          "Lying awake more than 30–45 minutes after going to bed",
          "Waking repeatedly, or waking at 3–4 AM unable to sleep again",
          "Excessive dreaming and waking unrefreshed",
          "Loud snoring, gasping or breath-holding noticed by family (possible sleep apnoea)",
          "Restless, crawling sensations in the legs at night",
          "Needing a tablet, alcohol or the phone to fall asleep",
        ],
      },
      {
        label: "During the day",
        items: [
          "Fatigue, heaviness and low mood",
          "Poor concentration and memory lapses at work",
          "Irritability with family over small things",
          "Dozing off while travelling or driving — a danger sign",
          "Headache on waking, dry mouth, acidity",
          "Anxiety through the evening about whether sleep will come",
        ],
      },
    ],
    causes: [
      "Anxiety, depression and chronic stress — by far the commonest cause",
      "Obstructive sleep apnoea, especially with snoring, obesity or a thick neck",
      "Thyroid disorders, anaemia, diabetes, chronic pain and prostate problems",
      "Alcohol, tobacco, cannabis and late-evening caffeine — alcohol fragments sleep badly",
      "Long-term sleeping-pill dependence, which eventually worsens sleep quality",
      "Shift duty, night-time screen use, irregular meal and sleep timings",
      "Restless legs syndrome and iron deficiency",
    ],
    treatments: [
      {
        title: "Finding the real cause",
        desc: "A detailed sleep history covering timings, substances, snoring and mood, with thyroid, haemoglobin and sugar testing where indicated — and a sleep study referral if apnoea is suspected.",
      },
      {
        title: "CBT-I — the gold standard",
        desc: "Sleep restriction, stimulus control, scheduled worry time and correction of sleep beliefs. Four to eight structured sessions, with gains that last years after the sessions end.",
      },
      {
        title: "Sleep hygiene that is actually practical",
        desc: "Fixed wake-up time, no clock-watching, bed used only for sleep, a caffeine cut-off and an evening wind-down that fits a real Indian household schedule.",
      },
      {
        title: "Tapering sleeping pills safely",
        desc: "If you have been on alprazolam, zolpidem or clonazepam for months or years, we substitute and taper gradually — never abruptly — while CBT-I takes over. This is routine work for us.",
      },
      {
        title: "Treating the driver, not just the symptom",
        desc: "Where insomnia sits on top of depression, anxiety or alcohol use, treating that condition properly is what finally restores sleep.",
        link: { to: "/conditions/depression", label: "Depression treatment" },
      },
      {
        title: "Review until sleep is genuinely restored",
        desc: "Follow-up every 2–4 weeks with a simple sleep diary, so improvement is measured rather than guessed.",
      },
    ],
    urgent:
      "If you doze off while driving or riding, treat it as urgent and get assessed before you next take the wheel. Loud snoring with daytime sleepiness needs a sleep study, not a sleeping pill.",
    faqs: [
      { q: "Are sleeping pills dangerous?", a: "Used briefly and under supervision they are reasonably safe. Taken nightly for months they cause tolerance, dependence, memory problems and falls in the elderly, and they steadily worsen sleep quality. Our aim is always to get you off them." },
      { q: "Can insomnia be cured without medication?", a: "Frequently, yes. CBT-I is at least as effective as medication in the short term and clearly better in the long term, because it corrects the behaviour and thinking that maintain the insomnia." },
      { q: "I have been on alprazolam for years. Can I stop?", a: "Yes, but never suddenly — abrupt stopping can cause dangerous withdrawal. We substitute a safer medication and taper over several weeks alongside CBT-I." },
      { q: "How many hours of sleep do I actually need?", a: "Most adults need 7–8 hours and some healthy people need only 6. The real measure is how you feel and function during the day, not the number on a tracker." },
      { q: "My husband snores loudly and is sleepy all day. Is that insomnia?", a: "That pattern suggests obstructive sleep apnoea — a different and potentially serious condition needing a sleep study. Sleeping tablets in that situation can be harmful." },
      { q: "Is it true that alcohol helps you sleep?", a: "Alcohol shortens the time to fall asleep and then wrecks the second half of the night, causing 3 AM awakenings, sweating and unrefreshing sleep. It is one of the commonest hidden causes of chronic insomnia we see." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/anxiety", label: "Anxiety & panic disorder" },
      { to: "/conditions/depression", label: "Depression treatment" },
    ],
    condName: "Insomnia",
    procedures: ["Insomnia treatment", "Cognitive behavioural therapy for insomnia (CBT-I)", "Hypnotic and benzodiazepine tapering", "Sleep apnoea screening", "Restless legs syndrome treatment"],
  },

  bipolar: {
    slug: "bipolar",
    eyebrow: "Bipolar Mood Disorder · OPD",
    h1a: "The goal is not a good month.",
    h1b: "It is a stable decade.",
    seoTitle: "Bipolar Disorder Treatment in Bhavnagar — Psychiatrist",
    seoDescription:
      "Bipolar mood disorder treatment in Bhavnagar at Manas Neuropsychiatry Hospital (since 1989). Mania and depression episodes, mood stabilisers, relapse prevention, family psychoeducation and mBPT for severe episodes. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/b1d8afb0bfe8383e3bf63c4a03c47489b071e8ae8731ce88c0913ecfedb97a51.jpeg",
    imageAlt: "A woman sitting on a terrace at dusk, half in warm light and half in shadow",
    caption: "Treated properly, most people with bipolar disorder work, marry, raise families and stay well for years.",
    intro:
      "Bipolar disorder is an illness of mood regulation in which phases of elevated mood (mania or hypomania) alternate with phases of depression, often with long well periods in between. It is frequently mistaken for an 'anger problem' or treated as plain depression — and antidepressants given alone can make it worse. With the right mood stabiliser and regular follow-up, long-term stability is a realistic expectation rather than a hope.",
    symptomGroups: [
      {
        label: "Manic or high phase",
        items: [
          "Unusually elevated, euphoric or irritable mood for days together",
          "Sleeping only 2–4 hours yet feeling energetic",
          "Talking rapidly, jumping between ideas, hard to interrupt",
          "Grand plans, over-spending, impulsive business or property decisions",
          "Increased religiosity, sexual behaviour or risk-taking",
          "Irritability and aggression when contradicted",
          "Belief in special powers or importance in severe mania",
        ],
      },
      {
        label: "Depressive phase",
        items: [
          "Deep low mood, hopelessness and guilt",
          "Sleeping excessively, or waking early unable to sleep again",
          "Complete loss of interest and energy, unable to attend work",
          "Slowed thinking and speech",
          "Suicidal thoughts — the most dangerous phase of the illness",
          "Craving for sweets and weight gain in some patients",
        ],
      },
    ],
    causes: [
      "A strong genetic component — bipolar disorder often runs in families",
      "Sleep deprivation, night shifts and long-distance travel as episode triggers",
      "Cannabis, alcohol and stimulant use, which can precipitate mania",
      "Antidepressants taken without a mood stabiliser",
      "Thyroid disease and steroid medication",
      "The post-partum period, a well-recognised high-risk time",
    ],
    treatments: [
      {
        title: "Getting the diagnosis right",
        desc: "We specifically ask about past high phases, which patients rarely report as a problem. Missing them is the commonest reason bipolar disorder is treated as depression for years.",
      },
      {
        title: "Mood stabilisers",
        desc: "Lithium, valproate or other agents chosen around your age, pregnancy plans and other illnesses, with the necessary blood-level and kidney, thyroid and liver monitoring built into follow-up.",
      },
      {
        title: "Treating acute episodes safely",
        desc: "Mania is treated urgently, on an outpatient basis where possible. Severe mania, catatonia or high suicide risk may need brief admission and occasionally modified brief pulse therapy.",
        link: { to: "/neuromodulation", label: "See mBPT & rTMS" },
      },
      {
        title: "Relapse prevention & mood charting",
        desc: "You and your family learn to recognise your own early warning signs — usually a drop in sleep — so an episode can be intercepted in days rather than weeks.",
      },
      {
        title: "Family psychoeducation",
        desc: "Families are taught what is illness and what is character, how to handle spending during a high phase, and why medication continues through well periods.",
      },
      {
        title: "Long-term, low-burden follow-up",
        desc: "Once stable, most patients need a review every 2–3 months. The aim is a normal working and family life, with the illness kept quietly in the background.",
      },
    ],
    urgent:
      "During a manic phase, large financial decisions should be paused and the patient should not travel or drive alone. During a depressive phase, any talk of suicide is an emergency — call +91 94275 60101.",
    faqs: [
      { q: "Is bipolar disorder curable?", a: "It is highly controllable rather than curable. With a mood stabiliser and regular follow-up, most patients have long stable periods and live completely normal working and family lives." },
      { q: "Why must I take medicine when I feel completely fine?", a: "Because the medicine is what is keeping you fine. Bipolar disorder relapses in the majority of patients who stop treatment during a well period — stopping is the commonest cause of a fresh episode." },
      { q: "Is lithium harmful to the kidneys?", a: "Lithium is one of the most effective mood stabilisers and is safe when monitored. We check blood levels along with kidney and thyroid function at planned intervals; monitored lithium is far safer than an untreated illness." },
      { q: "Can a person with bipolar disorder marry and have children?", a: "Yes, and thousands do. We plan medication around pregnancy in advance with your gynaecologist, since some mood stabilisers must be changed before conception." },
      { q: "My relative gets very angry and spends recklessly, but is not 'mad'. Could this be bipolar?", a: "Possibly. Irritable mania is common and is frequently dismissed as a temper problem. If the behaviour comes in distinct phases lasting days to weeks, with reduced sleep, it deserves a psychiatric assessment." },
      { q: "Do antidepressants help bipolar depression?", a: "Given alone they can trigger mania or rapid mood cycling. Bipolar depression is treated primarily with mood stabilisers; antidepressants are used cautiously and only under the cover of a stabiliser." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/depression", label: "Depression treatment" },
      { to: "/neuromodulation", label: "Neuromodulation options" },
    ],
    condName: "Bipolar Disorder",
    procedures: ["Bipolar disorder treatment", "Mood stabiliser therapy", "Lithium monitoring", "Family psychoeducation", "Modified brief pulse therapy (mBPT)"],
  },

  "adhd-children": {
    slug: "adhd-children",
    eyebrow: "ADHD in Children · Child & Adolescent OPD",
    h1a: "He is not a bad child.",
    h1b: "His brain runs faster than the class.",
    seoTitle: "ADHD Treatment for Children in Bhavnagar — Child Psychiatrist",
    seoDescription:
      "ADHD treatment for children in Bhavnagar at Manas Neuropsychiatry Hospital. Inattention, hyperactivity, falling grades, school complaints and temper problems assessed and treated with behaviour therapy, parent training and medication where needed. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/cd52e3d203b4711deea2abfa02b79b6749e97e2bfc69dda1e0baf45bb9e2243a.jpeg",
    imageAlt: "An Indian schoolboy distracted at his study table while his mother helps him",
    caption: "Roughly one child in twenty has ADHD. Most are called lazy or naughty for years before anyone assesses them.",
    intro:
      "Attention Deficit Hyperactivity Disorder is a developmental condition of attention, impulse control and activity level — not the result of bad parenting, too much television, or a lack of discipline. Untreated, it quietly damages school results, confidence and family peace for years. Treated, most children improve markedly, and the bulk of our work is behavioural training with parents and teachers rather than medication.",
    symptomGroups: [
      {
        label: "Inattention",
        items: [
          "Cannot stay with homework for more than a few minutes",
          "Careless mistakes despite knowing the answers",
          "Appears not to listen even when spoken to directly",
          "Loses pencils, books, water bottles and uniforms repeatedly",
          "Easily distracted by noise or movement; daydreams in class",
          "Avoids tasks needing sustained mental effort",
          "Performance far below what parents and teachers know he can do",
        ],
      },
      {
        label: "Hyperactivity & impulsivity",
        items: [
          "Cannot sit still; fidgets, climbs and runs indoors",
          "Talks excessively and blurts out answers",
          "Cannot wait for a turn; interrupts games and conversations",
          "Acts without thinking — running onto the road, jumping from heights",
          "Frequent school complaints about disturbing the class",
          "Temper outbursts and rapid frustration",
          "Difficulty making or keeping friends",
        ],
      },
    ],
    causes: [
      "Largely genetic — one parent often had similar childhood difficulties",
      "Prematurity, low birth weight and birth complications",
      "Lead exposure and, less commonly, thyroid problems",
      "Excessive screen time worsens symptoms but does not cause ADHD",
      "Conditions that mimic ADHD and must be excluded: hearing or vision problems, learning disability, absence seizures, anxiety, sleep deprivation, iron and vitamin deficiency, and autism spectrum disorder",
    ],
    treatments: [
      {
        title: "Proper assessment, not a five-minute label",
        desc: "Detailed developmental history, school report and teacher input, standardised rating scales, plus screening of hearing, vision, sleep, haemoglobin and thyroid — because several conditions look exactly like ADHD.",
      },
      {
        title: "Parent behaviour training",
        desc: "The most powerful intervention we have. Parents learn clear instruction-giving, immediate reward systems, consistent consequences, structured routines, and how to break the shouting cycle that exhausts everyone.",
      },
      {
        title: "School collaboration",
        desc: "We provide practical written recommendations for teachers — front-row seating, short broken-up tasks, movement breaks, extra time — and documentation for examination accommodations where appropriate.",
      },
      {
        title: "Behaviour therapy for the child",
        desc: "Attention-building activities, impulse-control games, social skills work and self-monitoring, pitched to the child's age.",
      },
      {
        title: "Medication where truly indicated",
        desc: "For moderate to severe ADHD, medication is safe, well-studied and often transformative for school performance. We start low, monitor height, weight, appetite and sleep at every visit, and always pair it with behavioural work.",
      },
      {
        title: "Treating what travels with ADHD",
        desc: "Learning disability, dyslexia, anxiety, oppositional behaviour, bedwetting and tics commonly co-exist and are addressed together — otherwise progress stalls.",
      },
    ],
    urgent:
      "If your child is being repeatedly beaten or humiliated at home or school for behaviour he cannot control, get an assessment now. The damage to self-esteem is often harder to treat than the ADHD itself.",
    faqs: [
      { q: "Is ADHD caused by too much mobile and television?", a: "No. ADHD is largely genetic and neurodevelopmental. Excessive screen time worsens attention, sleep and behaviour and should be reduced — but it is not the cause, and removing screens alone will not treat ADHD." },
      { q: "Will my child need medication?", a: "Not always. Mild ADHD is often managed with parent training, school changes and behaviour therapy. Moderate to severe ADHD usually responds best to medication plus behavioural work, and we discuss the decision with you fully before starting." },
      { q: "Are ADHD medicines addictive or harmful to a child's brain?", a: "At prescribed doses under supervision, the standard medicines are among the best-studied in child psychiatry and do not cause addiction. We monitor appetite, sleep, height and weight at every visit and take planned breaks where appropriate." },
      { q: "Will my child grow out of it?", a: "Hyperactivity usually reduces with age, while inattention and disorganisation often continue into adolescence and adulthood. Early treatment protects school results and self-esteem during the years that matter most." },
      { q: "My child concentrates for hours on video games. Can he still have ADHD?", a: "Yes — this is the commonest objection we hear. Fast-changing, highly rewarding activities hold attention easily; ADHD shows itself in boring, effortful, self-directed tasks like homework." },
      { q: "At what age can ADHD be diagnosed?", a: "Reliably from around 5–6 years, when school demands make the difficulty clear, though signs are often visible earlier. It is never too late — we also assess teenagers and adults." },
      { q: "Should I tell the school?", a: "Yes, with your consent it helps enormously. Teachers who understand the condition become allies instead of complainants, and simple classroom changes often produce visible improvement." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/anxiety", label: "Anxiety in children & adults" },
      { to: "/contact", label: "Book a child assessment" },
    ],
    condName: "Attention Deficit Hyperactivity Disorder in Children",
    procedures: ["ADHD assessment and treatment", "Parent behaviour training", "Behaviour therapy for children", "School-based intervention planning", "Stimulant and non-stimulant medication management"],
  },

  schizophrenia: {
    slug: "schizophrenia",
    eyebrow: "Schizophrenia & Psychosis · OPD & Inpatient",
    h1a: "It is an illness of the brain,",
    h1b: "not of the soul.",
    seoTitle: "Schizophrenia Treatment in Bhavnagar — Psychiatrist for Psychosis",
    seoDescription:
      "Schizophrenia and psychosis treatment in Bhavnagar at Manas Neuropsychiatry Hospital (since 1989). Hearing voices, suspiciousness, delusions and social withdrawal treated with antipsychotics, long-acting injections, family psychoeducation and rehabilitation. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/483ca36f9071610a0b63692f3f0bf5410baff86c19ee40d76884af9e0afa0b75.jpeg",
    imageAlt: "A young man sitting in a home courtyard with his mother nearby",
    caption: "Every year of untreated psychosis costs recovery. The first year of treatment decides the next twenty.",
    intro:
      "Schizophrenia and related psychotic illnesses change how the brain processes reality — producing voices, fixed false beliefs and withdrawal. It is not caused by black magic, curses, bad karma or a moral failing, and it does not improve with exorcism or bhuva treatment. It responds to medication, and the earlier treatment begins the better the long-term outcome. Many of our patients return to work, study and family life.",
    symptomGroups: [
      {
        label: "Positive symptoms",
        items: [
          "Hearing voices when nobody is present",
          "Firm belief that people are conspiring, poisoning food or watching",
          "Belief that thoughts are being controlled, read or broadcast",
          "Talking or laughing to oneself",
          "Disorganised, jumbled speech that is hard to follow",
          "Agitation or aggression arising from fear and suspicion",
        ],
      },
      {
        label: "Negative symptoms & decline",
        items: [
          "Withdrawal from friends, work and family conversation",
          "Loss of interest, initiative and emotional expression",
          "Neglect of bathing, clothing and grooming",
          "Sleeping by day, awake at night",
          "Falling academic or work performance over months",
          "Poor insight — genuinely not believing anything is wrong",
        ],
      },
    ],
    causes: [
      "Genetic vulnerability, often with a family history of mental illness",
      "Cannabis (ganja/charas) and stimulant use, which can precipitate and worsen psychosis",
      "Brain development factors, birth complications and head injury",
      "Severe or prolonged stress acting as a trigger in a vulnerable person",
      "Physical mimics we always rule out: thyroid disease, B12 deficiency, epilepsy, steroid effects, brain infections and tumours",
    ],
    treatments: [
      {
        title: "Assessment, and excluding physical causes",
        desc: "Detailed history from the patient and family, mental state examination, and blood or imaging tests where the presentation suggests a medical cause. Getting this right changes everything that follows.",
      },
      {
        title: "Antipsychotic medication",
        desc: "Started at the lowest effective dose with side-effects explained in advance. Voices and suspiciousness usually settle over 2–6 weeks; negative symptoms improve more slowly.",
      },
      {
        title: "Long-acting injectables",
        desc: "For patients who stop tablets when they feel better — the commonest cause of relapse — a once-monthly injection quietly protects recovery and ends the daily argument at home.",
      },
      {
        title: "Family psychoeducation",
        desc: "Families learn what is illness and what is behaviour, how to respond to delusions without arguing, how to reduce criticism at home, and how to spot an early relapse.",
      },
      {
        title: "Rehabilitation & return to work",
        desc: "Structured daily routine, social-skills work and graded return to study or occupation, so recovery means a life, not just quieter symptoms.",
      },
      {
        title: "Brief admission or mBPT when needed",
        desc: "Severe agitation, refusal to eat, catatonia or suicide risk may need short inpatient care and, rarely, modified brief pulse therapy under anaesthesia.",
        link: { to: "/neuromodulation", label: "See mBPT & rTMS" },
      },
    ],
    urgent:
      "If the patient is refusing food and water, is threatening self-harm or has become violent out of fear, do not wait for a 'good day' — call +91 94275 60101 for urgent assessment.",
    faqs: [
      { q: "Is schizophrenia caused by black magic or a curse?", a: "No. It is a brain illness with a strong genetic component. Years are commonly lost to bhuva and faith healing while the illness becomes harder to treat — treatment delayed is recovery lost." },
      { q: "Can the patient recover completely?", a: "Many patients recover well enough to work, marry and live independently, especially when treatment starts early and continues. A proportion have residual symptoms, which are still much milder on treatment than off it." },
      { q: "He is fine now. Can we stop the medicine?", a: "This is the single commonest reason for relapse. Medication is what is keeping him well; stopping it, especially in the first few years, brings the illness back in most patients. Reduction is possible but must be planned with us." },
      { q: "He refuses to take tablets and says nothing is wrong. What do we do?", a: "Poor insight is part of the illness, not stubbornness. The family can consult us alone first; often a long-acting monthly injection or a short admission breaks the deadlock, and insight returns as the illness improves." },
      { q: "Do antipsychotics make a person dull or 'sedated for life'?", a: "Older high doses could. Modern medication at correct doses aims for clear thinking and normal functioning; if a patient is over-sedated, the dose or drug needs changing — tell us rather than stopping." },
      { q: "Does ganja cause schizophrenia?", a: "Cannabis clearly increases the risk in vulnerable people, can trigger a first episode, and makes relapse much more likely. Stopping it is a non-negotiable part of treatment." },
      { q: "Can a person with schizophrenia marry?", a: "Many do, successfully. It should be a decision made with honesty, with the illness stable and the spouse informed — we counsel families through exactly this conversation." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/bipolar", label: "Bipolar disorder" },
      { to: "/neuromodulation", label: "Neuromodulation options" },
    ],
    condName: "Schizophrenia and Psychotic Disorders",
    procedures: ["Schizophrenia treatment", "Antipsychotic medication management", "Long-acting injectable antipsychotics", "Family psychoeducation", "Psychosocial rehabilitation"],
  },

  "stress-adjustment": {
    slug: "stress-adjustment",
    eyebrow: "Acute Stress & Adjustment Disorders · OPD",
    h1a: "Some storms need shelter,",
    h1b: "not a lifetime of tablets.",
    seoTitle: "Stress & Adjustment Disorder Treatment in Bhavnagar — Counselling",
    seoDescription:
      "Treatment for acute stress, adjustment disorder and grief in Bhavnagar at Manas Neuropsychiatry Hospital. Business loss, exam failure, bereavement, accidents, marital breakdown and job stress — short-term counselling and brief medication in OPD. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/720a185551d542e0a34af3db76a050666f87aba8fd787aa02e9f0ebc8061740c.jpeg",
    imageAlt: "A man sitting alone on steps outside a closed shop at dusk",
    caption: "Most people with an adjustment disorder need a handful of visits — not years of treatment.",
    intro:
      "An adjustment disorder is an intense, disabling reaction to an identifiable life event — a death, a business loss, an accident, a failed examination, a broken engagement, migration, or retirement. It is not weakness, and it is also not a permanent illness. Treated early, it usually settles within weeks; ignored, it frequently hardens into depression, alcohol use or a chronic anxiety disorder.",
    symptomGroups: [
      {
        label: "Emotional",
        items: [
          "Crying spells and a constant sense of dread",
          "Feeling unable to cope with routine work or household duties",
          "Repeatedly replaying the event in the mind",
          "Guilt or blame — 'I should have done something else'",
          "Irritability and short temper with family",
          "Hopelessness, and in some cases suicidal thoughts",
        ],
      },
      {
        label: "Physical & behavioural",
        items: [
          "Not sleeping or eating for days after the event",
          "Palpitations, tremor, headache, giddiness and acidity",
          "Avoiding the shop, road, hospital or person connected to the event",
          "Withdrawal from relatives, phone calls and social occasions",
          "Increased drinking, smoking or tobacco use to cope",
          "Absence from work, school or college",
        ],
      },
    ],
    causes: [
      "Bereavement and sudden death in the family",
      "Financial or business loss, debt and legal disputes",
      "Examination failure and academic pressure in students",
      "Marital conflict, separation, a broken engagement or dowry disputes",
      "Accidents, serious illness or surgery in self or a loved one",
      "Migration, job loss, transfer, retirement or an empty home after children leave",
    ],
    treatments: [
      {
        title: "One good, unhurried conversation first",
        desc: "A 45–60 minute consultation in which the whole story is heard. For many patients, being properly listened to by a doctor who is not in a hurry is itself the beginning of treatment.",
      },
      {
        title: "Brief, focused counselling",
        desc: "Problem-solving therapy and grief work over a few sessions — dealing with the actual problem in front of you rather than exploring your childhood.",
      },
      {
        title: "Short-term medication where needed",
        desc: "Sleep restoration and anxiety relief for a few weeks. We are deliberately cautious with habit-forming tablets, and we plan the stopping date when we start.",
      },
      {
        title: "Family and workplace support",
        desc: "Where appropriate, family members are guided on what helps and what makes it worse, and medical documentation is provided for leave from work or college.",
      },
      {
        title: "Protecting against alcohol and tobacco",
        desc: "Stress is when drinking and tobacco quietly begin. We screen for it early, because an adjustment disorder that turns into dependence is a far longer road.",
        link: { to: "/deaddiction", label: "Deaddiction programme" },
      },
      {
        title: "Review, then discharge",
        desc: "Two or three follow-ups to confirm recovery, with a clear plan of what to watch for. The goal is to finish treatment, not to continue it.",
      },
    ],
    urgent:
      "After a sudden loss or humiliation, any talk of suicide must be taken literally — this is when risk is highest. Call +91 94275 60101 the same day.",
    faqs: [
      { q: "Is it normal to feel this bad after a loss?", a: "Grief and distress after a major event are normal. It becomes a disorder when it is severe enough to stop you sleeping, eating, working or caring for your family for weeks — and that is the point at which treatment shortens the suffering." },
      { q: "Will I have to take medicine for years?", a: "Almost never. Adjustment disorders are usually treated for a few weeks to a few months. Many patients need counselling only." },
      { q: "How is this different from depression?", a: "An adjustment disorder is tied to a specific recent event and usually lifts as circumstances and coping improve. Depression becomes self-sustaining and persists even when circumstances improve — and it needs longer treatment." },
      { q: "My son failed his exam and has stopped eating and talking. Is this serious?", a: "Yes, it deserves an assessment now. Post-result distress in students is a well-recognised high-risk period for self-harm in India, and it responds very quickly to the right support." },
      { q: "Can counselling really help, or is it just talking?", a: "Structured counselling is not casual talking. Problem-solving therapy and grief work have measurable results, and for adjustment disorders they are the primary treatment rather than an add-on." },
      { q: "Is one visit enough?", a: "Often two or three visits are enough. We would rather see you briefly and discharge you than keep you in treatment unnecessarily." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/depression", label: "Depression treatment" },
      { to: "/conditions/insomnia", label: "Insomnia treatment" },
    ],
    condName: "Adjustment Disorder and Acute Stress Reaction",
    procedures: ["Adjustment disorder treatment", "Grief counselling", "Problem-solving therapy", "Crisis counselling", "Short-term pharmacotherapy"],
  },

  somatoform: {
    slug: "somatoform",
    eyebrow: "Somatoform, Pain & Tension Headache · OPD",
    h1a: "All your reports are normal.",
    h1b: "Your pain is still real.",
    seoTitle: "Unexplained Body Pain & Tension Headache Treatment in Bhavnagar",
    seoDescription:
      "Treatment for unexplained body pain, burning sensations, giddiness, tension headache and migraine at Manas Neuropsychiatry Hospital, Bhavnagar. When reports are normal but symptoms are real — somatoform and psychosomatic disorders treated in OPD. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/9ec23d398d85f4a6ba65190dbc73f2b6577e4bbe1fb1b4d1db53f7789070d850.jpeg",
    imageAlt: "A woman waiting in a clinic holding a bundle of old medical reports",
    caption: "Patients reach us after an average of five doctors and a file full of normal reports.",
    intro:
      "Somatoform and psychosomatic disorders are conditions in which genuine physical symptoms — pain, burning, giddiness, palpitations, headache, bowel upset — arise from the way the brain and nervous system process signals, rather than from damage to the organ being investigated. The symptoms are real, not imagined, and being told 'nothing is wrong, it is all in your mind' is both unhelpful and inaccurate. These conditions have specific, effective treatment.",
    symptomGroups: [
      {
        label: "Common presentations",
        items: [
          "Tension headache: a tight band or heaviness across the head, worse through the day",
          "Migraine with nausea, and sensitivity to light and sound",
          "Burning sensations in the hands, feet, head or whole body",
          "Body ache, neck and shoulder pain, low backache with normal scans",
          "Giddiness and unsteadiness with normal ENT and neurology reports",
          "Palpitations, chest heaviness and breathlessness with a normal ECG",
          "Gas, acidity, IBS-like loose motions or constipation",
        ],
      },
      {
        label: "The pattern around it",
        items: [
          "Symptoms worsen with stress, arguments and financial worry",
          "Multiple doctors consulted; repeated normal investigations",
          "Strong fear of a serious hidden disease such as cancer or a heart problem",
          "Frequent internet searching and reassurance-seeking",
          "Disturbed sleep, irritability and low mood alongside the pain",
          "Painkiller overuse — which itself causes daily headache",
        ],
      },
    ],
    causes: [
      "Underlying anxiety or depression, which amplifies pain signals",
      "Central sensitisation — the nervous system's pain filter becoming over-sensitive",
      "Chronic stress, caregiving burden, marital and financial conflict",
      "Analgesic overuse headache from daily painkillers",
      "Poor sleep, tobacco, alcohol and vitamin D or B12 deficiency",
      "A previous serious illness in self or family, which primes health anxiety",
    ],
    treatments: [
      {
        title: "Taking your symptoms seriously",
        desc: "We review the reports you already have, examine you, and clearly explain what is happening in the nervous system — usually replacing a decade of 'nothing is wrong' with an actual diagnosis.",
      },
      {
        title: "Stopping the investigation cycle",
        desc: "Repeating normal tests increases anxiety and strengthens the symptom. We agree on what genuinely still needs checking, and then deliberately stop.",
      },
      {
        title: "Medication that works on pain pathways",
        desc: "Low-dose antidepressants such as amitriptyline, duloxetine or SSRIs are used here as nerve-pain and headache treatments, not as 'mind medicines' — and they work.",
      },
      {
        title: "Withdrawing overused painkillers",
        desc: "Daily painkillers are a common hidden cause of chronic headache. We taper them with cover, and headaches often improve dramatically within weeks.",
      },
      {
        title: "Relaxation, CBT and physiotherapy",
        desc: "Progressive muscular relaxation, breathing work, graded activity and posture correction — the combination that reduces tension headache and body pain lastingly.",
      },
      {
        title: "Treating the anxiety or depression underneath",
        desc: "Where these are present, treating them properly is what finally settles the physical symptoms.",
        link: { to: "/conditions/anxiety", label: "Anxiety & panic disorder" },
      },
    ],
    urgent:
      "New severe headache with vomiting, weakness, visual loss, fever, seizures, or a first headache after the age of 50 must be evaluated medically first. We are not suggesting you ignore your body — only that a normal report deserves a proper explanation.",
    faqs: [
      { q: "Does this mean my pain is imaginary?", a: "No. The pain is genuinely experienced. What differs is the mechanism: the problem lies in how pain signals are generated and amplified by the nervous system, not in damage to the organ being scanned." },
      { q: "Why is a psychiatrist treating my headache and body pain?", a: "Because the medications and therapies that work best on chronic pain, tension headache and psychosomatic symptoms act on brain and nerve pathways — the area we specialise in. Neurologists and physicians refer such patients to us routinely." },
      { q: "Are the tablets you give painkillers?", a: "No. We mainly use low doses of medicines that calm over-sensitive pain pathways. They are not habit-forming and are usually taken for a few months, unlike the painkillers many patients have been swallowing daily for years." },
      { q: "I have already done MRI, ECG, endoscopy and blood tests. Should I do more?", a: "Usually not. Repeating normal tests deepens the anxiety that feeds the symptom. We will tell you honestly if anything genuinely remains to be checked." },
      { q: "How long does treatment take?", a: "Most patients notice clear improvement within 4–8 weeks, with treatment usually continuing for a few months and then tapering." },
      { q: "Is this the same as 'gas and weakness' that everyone talks about?", a: "Often, yes — much of what is described in Gujarati households as vaayu, gas, weakness or 'nas ni takleef' falls into this treatable group of conditions." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/anxiety", label: "Anxiety & panic disorder" },
      { to: "/conditions/depression", label: "Depression treatment" },
    ],
    condName: "Somatoform Disorder and Chronic Tension Headache",
    procedures: ["Somatoform disorder treatment", "Chronic pain management", "Tension headache and migraine treatment", "Analgesic overuse withdrawal", "Relaxation therapy and CBT"],
  },

  dementia: {
    slug: "dementia",
    eyebrow: "Dementia & Geriatric Psychiatry · OPD",
    h1a: "Forgetfulness in old age",
    h1b: "is not always 'just age'.",
    seoTitle: "Dementia & Memory Loss Treatment in Bhavnagar — Geriatric Psychiatry",
    seoDescription:
      "Dementia, memory loss and old-age psychiatric care in Bhavnagar at Manas Neuropsychiatry Hospital. Assessment for Alzheimer's, reversible causes, night-time restlessness, suspiciousness, late-life depression and caregiver support. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/32ae752b5fae841c75e44d28efc680a824d4c3778090e1bebf76b46d6f5585e0.jpeg",
    imageAlt: "An elderly man being shown an old photograph by his daughter",
    caption: "Roughly one in ten cases of memory loss has a treatable cause. Everyone deserves to be checked.",
    intro:
      "Dementia is a progressive decline in memory, language, judgement and daily function — most commonly Alzheimer's disease or vascular dementia. Ordinary ageing slows recall; dementia interferes with cooking, banking, medication and finding the way home. Some memory problems in the elderly are caused by thyroid disease, B12 deficiency, depression, medication or sleep apnoea, and these can improve substantially once treated — which is why assessment matters even when the diagnosis feels obvious.",
    symptomGroups: [
      {
        label: "Memory & thinking",
        items: [
          "Repeating the same question or story within minutes",
          "Forgetting recent events while remembering the distant past",
          "Difficulty handling money, bank work or medication",
          "Losing the way on familiar roads",
          "Struggling to find words, or using the wrong word",
          "Misplacing objects and then accusing others of theft",
        ],
      },
      {
        label: "Behaviour & mood (what families find hardest)",
        items: [
          "Suspiciousness towards a spouse, son or daughter-in-law",
          "Restlessness, wandering and confusion that worsens after sunset",
          "Day–night reversal and disturbed sleep for the whole household",
          "Irritability, shouting or hitting out when helped with bathing",
          "Seeing people or animals that are not there",
          "Apathy, tearfulness and withdrawal — often late-life depression alongside",
          "Neglect of hygiene, or repeated unsafe acts such as leaving the gas on",
        ],
      },
    ],
    causes: [
      "Alzheimer's disease — the commonest cause",
      "Vascular dementia from strokes, uncontrolled blood pressure and diabetes",
      "Lewy body and frontotemporal dementias, and Parkinson's disease",
      "Reversible contributors we always check: thyroid disease, B12 and folate deficiency, anaemia, sodium imbalance, sleep apnoea, chronic alcohol use, depression and sedative medication",
      "Head injury and chronic kidney or liver disease",
    ],
    treatments: [
      {
        title: "Cognitive assessment & scans",
        desc: "Structured memory testing, a full medication review and blood tests, with a CT or MRI where indicated — so the type of dementia and any treatable contributor is identified rather than assumed.",
      },
      {
        title: "Treating what can be reversed",
        desc: "Thyroid correction, B12 replacement, stopping sedatives, controlling blood pressure and diabetes, and treating late-life depression — which can mimic dementia entirely.",
      },
      {
        title: "Cognitive enhancers where appropriate",
        desc: "Medicines such as donepezil or memantine can slow decline and help function in suitable patients. We are honest about what they can and cannot do.",
      },
      {
        title: "Managing behaviour without over-sedating",
        desc: "Sunset restlessness, suspiciousness and aggression are first addressed with routine, light, hearing and vision correction and simple environmental changes; medication is used at the lowest dose and reviewed, since elderly brains are sensitive.",
      },
      {
        title: "Caregiver training & support",
        desc: "The family carries most of this illness. We teach practical handling of repeated questions, bathing refusal, wandering and night-time confusion — and we watch for caregiver burnout and depression, which is extremely common.",
      },
      {
        title: "Safety & legal planning",
        desc: "Guidance on stopping driving, gas and door safety, financial protection, and documentation for guardianship or disability benefits where needed.",
      },
    ],
    urgent:
      "Sudden confusion developing over hours or days is usually delirium — from infection, dehydration, low sodium or a medication — and is a medical emergency, not dementia. Get it assessed immediately.",
    faqs: [
      { q: "Is memory loss in old age normal?", a: "Slower recall is normal. Forgetting recent conversations, repeating questions, getting lost on known roads or being unable to manage money is not normal ageing and deserves assessment." },
      { q: "Is dementia curable?", a: "Most dementias cannot be cured, but the pace can often be slowed, treatable contributors corrected, and — most importantly — the distressing behaviours managed so the family can cope. Around one in ten cases has a substantially reversible cause." },
      { q: "My father accuses my mother of stealing and of an affair. Is he mad?", a: "This is a classic and well-recognised symptom of dementia, not a character change or a delusion to be argued with. It usually responds to a combination of environmental handling and careful low-dose medication." },
      { q: "Why does he become restless every evening?", a: "This is 'sundowning'. Good lighting, a fixed routine, limited daytime napping, no caffeine after evening and treatment of pain or constipation usually help significantly." },
      { q: "Should we tell the patient the diagnosis?", a: "In early dementia, honest disclosure allows the person to plan finances and wishes while they still can. We help families decide how and how much to tell." },
      { q: "Can he continue to drive or handle the bank?", a: "Usually not for long. We assess this specifically, because it protects both the patient and the family's finances and safety." },
      { q: "We are exhausted looking after him. Is there help?", a: "Yes — and caregiver exhaustion is a legitimate reason to consult. We provide practical training, arrange respite planning and treat caregiver depression, which we see constantly." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/depression", label: "Late-life depression" },
      { to: "/conditions/insomnia", label: "Sleep problems" },
    ],
    condName: "Dementia and Geriatric Psychiatric Disorders",
    procedures: ["Dementia assessment and treatment", "Cognitive testing", "Reversible cause screening", "Behavioural symptom management", "Caregiver training and support"],
  },

  "womens-mental-health": {
    slug: "womens-mental-health",
    eyebrow: "Women's Mental Health · OPD",
    h1a: "Becoming a mother",
    h1b: "should not cost you yourself.",
    seoTitle: "Post-partum Depression & Women's Mental Health in Bhavnagar",
    seoDescription:
      "Women's mental health care in Bhavnagar at Manas Neuropsychiatry Hospital — post-partum depression, PMDD, menopausal anxiety and insomnia, infertility distress and pregnancy-safe psychiatric medication. Private consultations. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/e48c1d4a3c2fba30e9004fd529a5d942868c3fc8d0ab2148ae60363a97497a88.jpeg",
    imageAlt: "A young mother sitting beside her sleeping newborn, looking tired",
    caption: "About one new mother in eight develops post-partum depression. Very few are ever asked about it.",
    intro:
      "Women's mental health needs care that accounts for hormones, pregnancy, breastfeeding and the particular pressures Indian women carry. We treat post-partum depression and psychosis, severe pre-menstrual mood change, menopausal anxiety and insomnia, infertility-related distress, and anxiety or depression where medication must be chosen around pregnancy. Consultations are private, and treatment choices are explained so that you decide with full information.",
    symptomGroups: [
      {
        label: "After childbirth",
        items: [
          "Persistent sadness or emptiness beyond the first two weeks",
          "Unable to feel love or bonding with the baby, and guilt about it",
          "Frightening intrusive thoughts about harm coming to the baby",
          "Crying spells, irritability and constant exhaustion",
          "Unable to sleep even when the baby sleeps",
          "Loss of appetite and weight",
          "Confusion, agitation, suspiciousness or hearing voices — post-partum psychosis, a medical emergency",
        ],
      },
      {
        label: "Across other life stages",
        items: [
          "Severe mood swings, irritability and despair in the week before periods (PMDD)",
          "Menopausal anxiety, hot flushes with palpitations, and broken sleep",
          "Distress, guilt and marital strain around infertility and repeated IVF cycles",
          "Anxiety or depression in pregnancy, often hidden for fear of medication",
          "Depression after hysterectomy or pregnancy loss",
          "Exhaustion and low mood from an unshared caregiving load at home",
        ],
      },
    ],
    causes: [
      "Rapid hormonal shifts after delivery, around periods and through menopause",
      "Sleep deprivation with a newborn — a powerful independent trigger",
      "A previous episode of depression, or a family history of it",
      "Thyroid disease and anaemia, both very common and easily missed after childbirth",
      "Lack of practical and emotional support at home, and pressure over the baby's sex",
      "Difficult pregnancy, emergency caesarean, pregnancy loss or an infant in NICU",
      "Domestic conflict or violence",
    ],
    treatments: [
      {
        title: "A private, unhurried consultation",
        desc: "A full assessment with thyroid, haemoglobin and vitamin testing where indicated — because after childbirth, thyroid disease and anaemia commonly masquerade as depression.",
      },
      {
        title: "Breastfeeding-safe medication",
        desc: "Several antidepressants have good safety data in breastfeeding. Where medication is needed we choose from these, explain the evidence, and coordinate with your paediatrician when helpful.",
      },
      {
        title: "Therapy and practical planning",
        desc: "CBT and interpersonal therapy, plus concrete work on sleep rotation, feeding support and dividing household load — because advice that ignores the reality of the home does not work.",
      },
      {
        title: "PMDD and menopausal treatment",
        desc: "Cycle charting, SSRIs used continuously or only in the luteal phase, and non-hormonal options for hot flushes and sleep, in coordination with your gynaecologist.",
      },
      {
        title: "Involving the family, carefully",
        desc: "Husbands and mothers-in-law are counselled about what post-partum depression is and is not — often the single change that most reduces a new mother's distress.",
      },
      {
        title: "Pre-pregnancy planning",
        desc: "If you are on psychiatric medication and planning a pregnancy, come before conceiving. Some drugs must be changed in advance, and stopping abruptly on your own is the riskier choice.",
      },
    ],
    urgent:
      "Post-partum psychosis — confusion, agitation, suspiciousness, hearing voices, or any thought of harming the baby or yourself — is a psychiatric emergency needing same-day assessment. Call +91 94275 60101.",
    faqs: [
      { q: "Is it normal to feel low after delivery?", a: "Mild 'baby blues' in the first two weeks is very common and settles on its own. Sadness, hopelessness or inability to function beyond two weeks is post-partum depression and needs treatment." },
      { q: "Can I take psychiatric medicine while breastfeeding?", a: "In many cases, yes. Several antidepressants pass into breast milk in very small amounts and have reassuring safety data. Untreated maternal depression carries its own real risks to mother and baby, so the decision is made by comparing both." },
      { q: "I get terrifying thoughts about my baby being harmed. Am I dangerous?", a: "Unwanted intrusive thoughts are extremely common in new mothers and are the opposite of intention. They are a treatable anxiety symptom. If thoughts come with confusion, voices or a wish to act, that is an emergency — call us immediately." },
      { q: "Is PMDD a real condition?", a: "Yes. Severe, cyclical mood change that disrupts work and relationships in the week before periods is a recognised disorder with proven treatment — it is not simply 'moodiness'." },
      { q: "Will my family have to know?", a: "Not unless you want them to. Consultations are confidential. We do recommend involving at least one supportive family member for practical help, but that is your choice." },
      { q: "I am going through IVF and I am breaking down. Can you help?", a: "Yes. Infertility treatment carries a heavy, rarely acknowledged emotional load. We provide counselling and, where needed, treatment that is compatible with your fertility protocol." },
      { q: "Can depression be treated during pregnancy?", a: "Yes, and it should be. Untreated depression in pregnancy is associated with poorer outcomes for both mother and baby. Therapy is first-line, with carefully chosen medication when severity requires it." },
    ],
    related: [
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
      { to: "/conditions/depression", label: "Depression treatment" },
      { to: "/sexology", label: "Sexual health & intimacy" },
    ],
    condName: "Post-partum Depression and Women's Mental Health Disorders",
    procedures: ["Post-partum depression treatment", "Premenstrual dysphoric disorder (PMDD) treatment", "Perinatal psychiatric medication planning", "Menopausal anxiety and insomnia treatment", "Infertility-related counselling"],
  },

  "suicide-crisis": {
    slug: "suicide-crisis",
    eyebrow: "Suicide Risk & Crisis Care · Same-day",
    h1a: "If you are thinking of ending it,",
    h1b: "call us before you decide.",
    seoTitle: "Suicide Prevention & Psychiatric Emergency Help in Bhavnagar",
    seoDescription:
      "Same-day psychiatric crisis care and suicide risk assessment in Bhavnagar at Manas Neuropsychiatry Hospital. Emergency support 24×7, safety planning, rapid-acting treatment including ketamine infusion for active suicidal ideation. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/1f74cb66b33172747ec01c7ab8236650e133bb525799299c3e08ae26a67ad65c.jpeg",
    imageAlt: "Two hands clasped together across a table in warm lamp light",
    caption: "Suicidal thinking is almost always temporary. The decision it pushes you towards is not.",
    intro:
      "Suicidal thoughts are a symptom — of depression, alcohol use, psychosis, chronic pain, humiliation or unbearable circumstance — and they are treatable. Most people who survive an attempt say the intense urge lasted minutes to hours. That is exactly why reaching help in those hours changes everything. If you are in that place right now, call +91 94275 60101. If you are worried about someone else, you can call us on their behalf.",
    symptomGroups: [
      {
        label: "Warning signs in someone you love",
        items: [
          "Talking about being a burden, or that everyone would be better off without them",
          "Saying goodbye, giving away belongings, settling accounts or writing notes",
          "Searching for or acquiring pesticide, tablets, rope or a weapon",
          "A previous attempt — the single strongest risk factor",
          "Sudden calm or cheerfulness after weeks of despair, once a decision has been made",
          "Withdrawal, hopelessness, and 'nothing will ever change'",
          "Increased drinking; sleeplessness for several nights together",
        ],
      },
      {
        label: "High-risk situations",
        items: [
          "Severe depression, bipolar depression or psychosis with command voices",
          "Alcohol or drug intoxication, which removes the last restraint",
          "Examination failure, results day and academic humiliation in students",
          "Business collapse, debt, moneylender pressure and legal cases",
          "Marital breakdown, a broken relationship or public shame",
          "Chronic pain, cancer and terminal illness",
          "Recent discharge after a psychiatric admission — a known vulnerable window",
        ],
      },
    ],
    causes: [
      "Depression and bipolar disorder — the commonest treatable drivers",
      "Alcohol and substance use, present in a large share of attempts",
      "Psychosis with command hallucinations",
      "Impulsivity during an acute crisis, often with no prior mental illness",
      "Access to pesticide and hoarded medication in the home",
      "Isolation, and the belief that help would bring disgrace to the family",
    ],
    treatments: [
      {
        title: "Same-day assessment",
        desc: "We see suicidal-risk patients urgently. The consultation is calm and non-judgemental — nobody is scolded, lectured or forced to justify themselves.",
      },
      {
        title: "Safety planning",
        desc: "A concrete, written plan: who to call, where to go, what to do in the next few hours, and removal of pesticide, hoarded tablets, ropes and weapons from the home. Means restriction saves lives.",
      },
      {
        title: "Treating the driver fast",
        desc: "Depression, psychosis, alcohol withdrawal or insomnia are treated aggressively and simultaneously, because the thoughts fade as the illness lifts.",
      },
      {
        title: "Rapid-acting options",
        desc: "For active suicidal ideation in severe depression, ketamine infusion can reduce suicidal thinking within hours, and modified brief pulse therapy is available where the situation is life-threatening.",
        link: { to: "/neuromodulation", label: "Rapid-acting treatments" },
      },
      {
        title: "Family involvement & supervision",
        desc: "With your consent, one or two family members are taught how to supervise medication, stay with the patient, and respond without anger or blame — which is what most families genuinely do not know how to do.",
      },
      {
        title: "Close follow-up",
        desc: "Frequent reviews through the risky weeks, with phone access between visits. The first month after a crisis is when contact matters most, and it is exactly when most patients are lost to follow-up.",
      },
    ],
    urgent:
      "If there is immediate danger, or if poison or tablets have already been consumed, go straight to the nearest emergency room or call an ambulance first — medical stabilisation comes before psychiatric care. Then call us on +91 94275 60101. Do not leave the person alone, and remove pesticides, medicines and sharp objects now.",
    faqs: [
      { q: "If I tell a doctor I am suicidal, will I be locked up?", a: "No. The great majority of patients with suicidal thoughts are treated as outpatients with a safety plan and family support. Admission is used only when risk is immediate and cannot be managed at home, and it is discussed with you." },
      { q: "Will the police be involved?", a: "Attempted suicide is no longer treated as a crime in India, and under the Mental Healthcare Act 2017 a person who attempts suicide is presumed to have severe stress and is entitled to care, not punishment." },
      { q: "Does asking someone directly about suicide put the idea in their head?", a: "No — this is a harmful myth. Asking directly and calmly reduces distress and is the single most useful thing a family member can do. Ask plainly: 'Are you thinking of ending your life?'" },
      { q: "He says he wants to die but has not done anything. Is that serious?", a: "Yes. Take every statement seriously, keep him company, remove access to pesticide and medicines, and get an assessment today. Never dismiss it as attention-seeking or drama." },
      { q: "Will this be kept confidential?", a: "Yes. Nothing is disclosed outside the clinic without your written consent, except in the narrow circumstances where life is in immediate danger." },
      { q: "Can these thoughts really go away?", a: "In most patients, yes — often substantially within weeks of proper treatment. Survivors of attempts overwhelmingly report being glad they survived once the illness was treated." },
      { q: "What if the person refuses to come?", a: "Come yourself. We advise families regularly on how to open the conversation, how to make the home safer tonight, and how to bring someone in without a confrontation." },
    ],
    related: [
      { to: "/conditions/depression", label: "Depression treatment" },
      { to: "/neuromodulation", label: "Ketamine & mBPT" },
      { to: "/general-psychiatry", label: "All general psychiatry conditions" },
    ],
    condName: "Suicidal Ideation and Psychiatric Emergency",
    procedures: ["Suicide risk assessment", "Crisis intervention and safety planning", "Ketamine infusion therapy", "Modified brief pulse therapy (mBPT)", "Emergency psychiatric care"],
  },
};

export const conditionJsonLd = (c) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE}/conditions/${c.slug}`,
      url: `${SITE}/conditions/${c.slug}`,
      name: c.seoTitle,
      headline: `${c.h1a} ${c.h1b}`,
      description: c.seoDescription,
      inLanguage: "en-IN",
      specialty: "Psychiatry",
      audience: { "@type": "Patient", healthCondition: { "@type": "MedicalCondition", name: c.condName } },
      primaryImageOfPage: { "@type": "ImageObject", url: c.image, caption: c.imageAlt },
      about: {
        "@type": "MedicalCondition",
        name: c.condName,
        possibleTreatment: c.procedures.map((p) => ({ "@type": "MedicalTherapy", name: p })),
        signOrSymptom: c.symptomGroups
          .flatMap((g) => g.items)
          .slice(0, 12)
          .map((s) => ({ "@type": "MedicalSignOrSymptom", name: s })),
        riskFactor: c.causes.slice(0, 8).map((r) => ({ "@type": "MedicalRiskFactor", name: r })),
      },
      mainContentOfPage: {
        "@type": "WebPageElement",
        cssSelector: `[data-testid="condition-page-${c.slug}"]`,
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "General Psychiatry", item: `${SITE}/general-psychiatry` },
          { "@type": "ListItem", position: 3, name: c.condName, item: `${SITE}/conditions/${c.slug}` },
        ],
      },
      isPartOf: { "@id": `${SITE}/#website` },
      publisher: clinicNode,
      provider: clinicNode,
    },
    faqNode(c.faqs),
  ],
});
